import { html, nothing } from 'lit';

import '../../library/editors/elements/d-edit-textarea.js';
import '../../library/editors/elements/d-select-tag.js';
import '../../library/elements/d-section.js';
import { customElement, property, query } from 'lit/decorators.js';
import type { DPopup } from 'src/library/components/d-popup.js';
import type { EntityOption } from 'src/layout/application-view-model.js';
import { BaseDialog } from 'src/library/components/BaseDialog.js';
import type { ShareContentInput } from './share-content-input';
import type { ShareContentResult } from './share-content-result';
import type { SelectTagOption } from '../../library/editors/elements/select-tag-option';
import type { DialogActionInput } from 'src/library/components/DialogActionInput';
import { t } from 'src/i18n';

/**
 *
 * STATUS OK
 */
@customElement('d-share-content-dialog')
export class DShareContentDialog extends BaseDialog<ShareContentInput, void> {
  static readonly styles = [...BaseDialog.styles];

  @property({ type: Array })
  employees: EntityOption[] = [];
  @property({ type: Array })
  contacts: EntityOption[] = [];
  @property({ type: Array })
  recipientEmployees: string[] = [];
  @property({ type: Array })
  recipientContacts: string[] = [];
  @property({ type: Array })
  paths: { href: string }[] = [];
  @property({ type: String })
  message = '';
  @query('d-popup')
  popup!: DPopup;
  width = 600;
  title = t('shareContent.title');
  private info = t('shareContent.info');

  protected get calculatedFooterActions(): DialogActionInput[] {
    return [
      { name: t('common:cancel'), action: 'cancel' },
      { name: t('layout.send'), action: 'send', main: true, disabled: this.sendDisabled },
    ];
  }

  private get employeeOptions(): SelectTagOption[] {
    return this.employees.map((e) => {
      return { value: e.uuid, text: e.name, disabled: e.disabled };
    });
  }

  private get contactOptions(): SelectTagOption[] {
    return this.contacts.map((e) => {
      return { value: e.uuid, text: e.name, disabled: e.disabled };
    });
  }

  private get recipients() {
    return this.recipientEmployees.concat(this.recipientContacts);
  }

  private get sendDisabled() {
    return (
      this.recipients.filter((r) => {
        return (
          this.employees.concat(this.contacts).filter((e) => {
            return e.uuid === r && !e.disabled;
          }).length > 0
        );
      }).length === 0
    );
  }

  renderBody() {
    return html`
      <d-section>
        <d-view-info .content=${this.info}></d-view-info>
      </d-section>
      <d-section topless>
        <d-edit-textarea
          max-rows="50"
          placeholder="${t('layout.requestSignatures.messageComment')}"
          value="${this.message}"
          @value-changed=${(e) => {
            this.message = e.detail.value;
          }}
        ></d-edit-textarea>
      </d-section>
      <d-section topless>
        <d-select-tag
          label="${this.contacts.length ? t('shareContent.shareWithStaff') : t('shareContent.shareWith')}"
          .options=${this.employeeOptions}
          .value=${this.recipientEmployees}
          .toggleAll=${this.employeeOptions.length > 2}
          @value-changed=${(e) => {
            this.recipientEmployees = e.detail.value;
          }}
        >
        </d-select-tag>
      </d-section>
      ${this.contacts.length
        ? html` <d-section topless>
            <d-select-tag
              label="${t('shareContent.shareWithContacts')}"
              .options=${this.contactOptions}
              .value=${this.recipientContacts}
              .toggleAll=${this.contactOptions.length > 2}
              @value-changed=${(e) => {
                this.recipientContacts = e.detail.value;
              }}
            >
            </d-select-tag>
          </d-section>`
        : nothing}
    `;
  }

  protected fetchResult(detail: string | undefined): ShareContentResult {
    return {
      action: detail === 'send' ? 'send' : 'cancel',
      message: detail === 'send' ? this.message : '',
      recipients: detail === 'send' ? this.recipients : [],
    };
  }

  protected initializeDialog(input: ShareContentInput) {
    console.log('initializeDialog', input);
    this.employees = input.employees;
    this.contacts = input.contacts;
    this.message = input.message ?? '';
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-share-content-dialog': DShareContentDialog;
  }
}
